/* Routes (docs/WEB-APP-MASTER-PLAN.md §5.1). */
import { useEffect } from "react";
import { Outlet, type RouteComponent, createRootRoute, createRoute, createRouter, lazyRouteComponent, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMe } from "./api";
import { useLang, useT } from "./i18n";
import { Spinner } from "./components/ui";

// Each area is its own chunk (roadmap v2 P16): a child's lesson does not download the parent area, the legal texts or
// onboarding. Routes preload on intent (hover/touch), so a click rarely waits.
const onboarding = () => import("./screens/onboarding");
const kid = () => import("./screens/kid");
const parent = () => import("./screens/parent");
const legal = () => import("./screens/legal");
const cancel = () => import("./screens/cancel");
const auth = () => import("./screens/auth");
const withdraw = () => import("./screens/withdraw");
const Start = lazyRouteComponent(onboarding, "Start");
const Confirm = lazyRouteComponent(onboarding, "Confirm");
const ChildForm = lazyRouteComponent(onboarding, "ChildForm");
const NewChild = lazyRouteComponent(() => onboarding().then(m => ({ default: () => <m.ChildForm afterSave="/parent" /> })));
const Consent = lazyRouteComponent(onboarding, "Consent");
const PinSetup = lazyRouteComponent(onboarding, "PinSetup");
const Handoff = lazyRouteComponent(onboarding, "Handoff");
const KidHome = lazyRouteComponent(kid, "KidHome");
const MicCheck = lazyRouteComponent(kid, "MicCheck");
const Lesson = lazyRouteComponent(kid, "Lesson");
const Done = lazyRouteComponent(kid, "Done");
const Dashboard = lazyRouteComponent(parent, "Dashboard");
const ChildPage = lazyRouteComponent(parent, "ChildPage");
const Report = lazyRouteComponent(parent, "Report");
const PlanPage = lazyRouteComponent(parent, "PlanPage");
const Privacy = lazyRouteComponent(parent, "Privacy");
const Settings = lazyRouteComponent(parent, "Settings");
const SignIn = lazyRouteComponent(auth, "SignIn");
const Terms = lazyRouteComponent(legal, "Terms");
const PrivacyNotice = lazyRouteComponent(legal, "PrivacyNotice");
const Impressum = lazyRouteComponent(legal, "Impressum");
const CancelPage = lazyRouteComponent(cancel, "CancelPage");
const WithdrawPage = lazyRouteComponent(withdraw, "WithdrawPage");

const str = (v: unknown) => (typeof v === "string" && v.length < 200 ? v : undefined);

function Root() {
  const me = useMe();
  const { lang, setLang } = useLang();
  const ui = me.data?.family?.ui;
  useEffect(() => { if (ui && ui !== lang) setLang(ui); }, [ui, lang, setLang]);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const kid = useRouterState({ select: s => s.location.pathname.startsWith("/kid") });
  return (
    <>
      {!kid && me.data && !me.data.config.liveEnabled && Date.now() < me.data.config.launchAt * 1000 && <PrelaunchBanner lang={lang} launchAt={me.data.config.launchAt} />}
      <Outlet />
      {!kid && <LegalFooter />}
    </>
  );
}

/** Lessons open at the launch (5 Oct 2026, 09:00 TRT, or the flag `launch_at` when it moves); until then the parents'
    pages say so and point to the founding-families list on the marketing site. */
function PrelaunchBanner({ lang, launchAt }: { lang: string; launchAt: number }) {
  const t = useT();
  const date = new Date(launchAt * 1000).toLocaleDateString(lang === "tr" ? "tr-TR" : lang === "de" ? "de-DE" : "en-GB", { day: "numeric", month: "long", timeZone: "Europe/Istanbul" });
  const site = `https://lumikids.live/${lang === "tr" ? "" : `${lang}/`}?utm_source=app&utm_medium=banner&utm_campaign=launch-2026-10`;
  return (
    <div className="bg-purple text-white text-center text-sm font-bold px-4 py-2" role="status">
      {t("launch.banner", { date })} <a className="underline" href={site}>{t("launch.bannerLink")}</a>
    </div>
  );
}

/** Terms, privacy, the cancel page and the withdrawal function from every page outside kid mode (both must be easy to
    find: § 312k BGB, EU Directive 2023/2673). */
function LegalFooter() {
  const t = useT();
  return (
    <footer className="mx-auto max-w-5xl px-4 py-6 text-sm text-ink-soft flex flex-wrap gap-x-4 gap-y-1 justify-center">
      <a className="underline" href="/legal/terms">{t("confirm.termsLink")}</a>
      <a className="underline" href="/legal/privacy">{t("confirm.privacyLink")}</a>
      <a className="underline" href="/legal/impressum">{t("legal.impressumLink")}</a>
      <a className="underline font-bold" href="/cancel">{t("cancel.link")}</a>
      <a className="underline font-bold" href="/withdraw">{t("withdraw.link")}</a>
    </footer>
  );
}

function Landing() {
  const nav = useNavigate();
  const me = useMe();
  useEffect(() => {
    if (!me.data) return;
    if (!me.data.family) void nav({ to: "/start", replace: true });
    else if (me.data.kid) void nav({ to: "/kid", replace: true });
    else if (!me.data.children.length) void nav({ to: "/onboarding/child", replace: true });
    else void nav({ to: "/parent", replace: true });
  }, [me.data, nav]);
  return <Spinner />;
}

function Redirect({ to }: { to: "/parent/plan" }) {
  const nav = useNavigate();
  useEffect(() => { void nav({ to, replace: true }); }, [nav, to]);
  return <Spinner />;
}

const root = createRootRoute({ component: Root });
const r = <P extends string>(path: P, component: RouteComponent) => createRoute({ getParentRoute: () => root, path, component });

const childSearch = { validateSearch: (s: Record<string, unknown>): { child?: string } => ({ child: str(s.child) }) };
const lessonSearch = { validateSearch: (s: Record<string, unknown>): { lesson?: string } => ({ lesson: str(s.lesson) }) };

const routeTree = root.addChildren([
  r("/", Landing),
  createRoute({ getParentRoute: () => root, path: "/start", component: Start, validateSearch: (s: Record<string, unknown>): { target?: string; home?: string; age?: number } => ({ target: str(s.target), home: str(s.home), age: Number.isInteger(Number(s.age)) ? Number(s.age) : undefined }) }),
  r("/onboarding/confirm", Confirm),
  r("/onboarding/child", ChildForm),
  createRoute({ getParentRoute: () => root, path: "/onboarding/consent", component: Consent, ...childSearch }),
  createRoute({ getParentRoute: () => root, path: "/onboarding/pin", component: PinSetup, ...childSearch }),
  createRoute({ getParentRoute: () => root, path: "/onboarding/handoff", component: Handoff, ...childSearch }),
  r("/kid", KidHome),
  createRoute({ getParentRoute: () => root, path: "/kid/check", component: MicCheck, ...lessonSearch }),
  createRoute({ getParentRoute: () => root, path: "/kid/lesson", component: Lesson, ...lessonSearch }),
  r("/kid/done/$run", Done),
  r("/parent", Dashboard),
  r("/parent/child/new", NewChild),
  r("/parent/child/$id", ChildPage),
  r("/parent/report/$run", Report),
  createRoute({ getParentRoute: () => root, path: "/parent/plan", component: PlanPage, validateSearch: (s: Record<string, unknown>): { checkout?: string; session?: string } => ({ checkout: str(s.checkout), session: str(s.session) }) }),
  r("/parent/minutes", () => <Redirect to="/parent/plan" />),
  r("/cancel", CancelPage),
  r("/withdraw", WithdrawPage),
  r("/parent/privacy", Privacy),
  r("/parent/settings", Settings),
  createRoute({ getParentRoute: () => root, path: "/login", component: SignIn, validateSearch: (s: Record<string, unknown>): { next?: string } => ({ next: str(s.next) }) }),
  // Before accounts, a family moved browsers here; signing in does that now.
  r("/restore", SignIn),
  r("/legal/terms", Terms),
  r("/legal/privacy", PrivacyNotice),
  r("/legal/impressum", Impressum),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent", defaultPendingComponent: Spinner });

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}
